import {ApolloClient, createHttpLink, from, InMemoryCache, Observable} from '@apollo/client';
import {setContext} from '@apollo/client/link/context';
import {onError} from '@apollo/client/link/error';
import {getAuth} from 'firebase/auth';
import {refreshAuthToken} from './auth';
import toast from 'react-hot-toast';

let authClient: ApolloClient<any>;

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const extensions = (err as any).extensions;
      const status = extensions?.violations?.[0]?.status;

      if (status === 403 && !operation.getContext().isRetry) {
        return new Observable(observer => {
          refreshAuthToken()
            .then(token => {
              if (!token) {
                observer.error(new Error('Session expired'));
                return;
              }
              operation.setContext({
                headers: {
                  ...operation.getContext().headers,
                  authorization: `Bearer ${token}`,
                },
                isRetry: true,
              });
              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            })
            .catch(error => observer.error(error));
        });
      }

      console.error('Auth API GraphQL Error:', err);
    }
  }

  if (networkError) {
    console.error('Auth API Network Error:', networkError);
    if (networkError.message?.includes('Failed to fetch')) {
      toast.error('Unable to connect to Auth API.', {
        id: 'auth-api-down',
        duration: 5000,
      });
    }
  }
});

const httpLink = createHttpLink({
  uri: `${import.meta.env.VITE_AUTH_API_URL}/graphql`,
});

const authLink = setContext(async (_, { headers }) => {
  const auth = getAuth();
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
  const tenancy = sessionStorage.getItem('yojo_tenancy');

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      ...(tenancy ? { 'x-yojo-tenancy': tenancy } : {}),
    },
  };
});

export const createAuthApolloClient = () => {
  authClient = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { fetchPolicy: 'no-cache', nextFetchPolicy: 'no-cache' },
      query: { fetchPolicy: 'no-cache' },
      mutate: { fetchPolicy: 'no-cache' },
    },
    queryDeduplication: false,
  });
  return authClient;
};

export const getAuthClient = () => authClient;
